"use client";

import { useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Icon } from "@/lib/icons/Icon";
import { Field, FormStatus, Input, Select } from "@/components/ui/Form";
import { Badge } from "@/components/ui/Badge/Badge";
import { fetchUserProjects, type UserProject } from "@/lib/api/c-crm-project-management-user";
import { pluginRouteHref } from "./taskRoute";
import styles from "./CCrmProjectManagement.module.css";

const PAGE_SIZE = 20;

const STATE_ALL = "all";
const STATE_ACTIVE = "active";
const STATE_ENDED = "ended";
type StateFilter = typeof STATE_ALL | typeof STATE_ACTIVE | typeof STATE_ENDED;

const STAGE_ALL = "all";

/**
 * Zakładka "Projekty" pluginu c_crm_project_management (shell c_user_accounts).
 *
 * Akcja hash-routable (z `sharingContentForApi`): `projects`. Zakres: projekty
 * w których user jest właścicielem, klientem albo członkiem zespołu - liczy to
 * backend (`IMODEL_M_CRM_PROJECTS::getUserProjects`).
 *
 * **Grid:** TanStack Table v8 (headless) - ten sam wzorzec co lista faktur
 * (`CFactureView`): cały bounded zbiór pobrany raz, sort/filtr/paginacja
 * client-side. Kolejne fazy dołożą repozytorium dokumentów i dziennik prac.
 *
 * **Wejście w zadania** odbywa się per wiersz (kolumna Zadania) - obsługę stanu
 * trzyma `CCrmProjectManagementView`, ta lista tylko zgłasza wybór przez callbacki.
 */

/** Y-m-d H:i:s -> Y-m-d (data bez czasu). Defensywnie zwraca wejście gdy brak spacji. */
function formatDate(raw: string): string {
  if (!raw) return "";
  return raw.split(" ")[0] || raw;
}

/** Etykieta etapu - nazwa ze słownika, a gdy jej brak, sam numer (bez udawania że etapu nie ma). */
function stageLabel(p: UserProject): string {
  if (p.stage_name) return p.stage_name;
  return p.stage > 0 ? `Etap ${p.stage}` : "-";
}

export function ProjectsList({
  onOpenTasks,
  onAddTask,
}: {
  /** Wejście w listę zadań projektu. */
  onOpenTasks: (projectId: number) => void;
  /** Wejście wprost w formularz zgłoszenia zadania. */
  onAddTask: (projectId: number) => void;
}) {
  const [rows, setRows] = useState<UserProject[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [stateFilter, setStateFilter] = useState<StateFilter>(STATE_ACTIVE);
  const [stageFilter, setStageFilter] = useState<string>(STAGE_ALL);
  const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { projects, truncated: tr } = await fetchUserProjects();
        if (cancelled) return;
        setRows(projects);
        setTruncated(tr);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* Lista etapów do selecta - budowana z faktycznych danych, żeby nie pokazywać
     etapów których user nie ma w żadnym projekcie. */
  const stageOptions = useMemo(() => {
    const seen = new Map<number, string>();
    rows.forEach((r) => {
      if (!seen.has(r.stage)) seen.set(r.stage, stageLabel(r));
    });
    return [...seen.entries()].sort((a, b) => a[0] - b[0]);
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (stateFilter === STATE_ACTIVE && r.ended) return false;
      if (stateFilter === STATE_ENDED && !r.ended) return false;
      if (stageFilter !== STAGE_ALL && String(r.stage) !== stageFilter) return false;
      return true;
    });
  }, [rows, stateFilter, stageFilter]);

  const hasActiveFilters = stateFilter !== STATE_ACTIVE || stageFilter !== STAGE_ALL;

  const columns = useMemo(() => {
    const col = createColumnHelper<UserProject>();
    return [
      /* Numer projektu w TEJ SAMEJ komorce co nazwa (nad tytulem), a nie w osobnej
         kolumnie - osobna kolumna zjadala szerokosc, przez ktora tabela wychodzila
         poza ekran. Accessor zwraca numer RAZEM z nazwa, zeby wyszukiwarka nadal
         znajdowala po numerze; sortowanie jawnie po samej nazwie, bo inaczej
         kolumna "Projekt" sortowalaby sie po numerze. */
      col.accessor((r) => `${r.nr} ${r.name}`, {
        id: "name",
        header: () => "Projekt",
        sortingFn: (a, b) => a.original.name.localeCompare(b.original.name, "pl"),
        cell: (ctx) => {
          const r = ctx.row.original;
          return (
            <span className={styles.nameCell}>
              <span className={styles.rowNr}>{r.nr}</span>
              <span className={styles.name}>{r.name}</span>
              {r.domain_online && <span className={styles.domain}>{r.domain_online}</span>}
            </span>
          );
        },
      }),
      col.accessor((r) => stageLabel(r), {
        id: "stage",
        header: () => "Etap",
        cell: (ctx) => <Badge>{ctx.getValue()}</Badge>,
      }),
      col.accessor((r) => r.role_name ?? (r.is_client ? "Klient" : "-"), {
        id: "role",
        header: () => "Twoja rola",
      }),
      col.accessor((r) => r.project_manager?.fullname ?? "", {
        id: "project_manager",
        header: () => "Opiekun projektu",
        /* Imie plus kontakt - klient ma wiedziec, do kogo sie zglosic, bez szukania
           w mailach. Telefon i mail jako odnosniki, zeby dzialaly na telefonie. */
        cell: (ctx) => {
          const pm = ctx.row.original.project_manager;
          if (!pm) return <span className={styles.unassigned}>Nieprzydzielony</span>;
          return (
            <span className={styles.pmCell}>
              <span className={styles.name}>{pm.fullname}</span>
              {pm.email && (
                <a className={styles.contactLink} href={`mailto:${pm.email}`}>
                  {pm.email}
                </a>
              )}
              {pm.telephone && (
                <a className={styles.contactLink} href={`tel:${pm.telephone.replace(/\s+/g, "")}`}>
                  {pm.telephone}
                </a>
              )}
            </span>
          );
        },
      }),
      col.accessor((r) => r.start_date ?? "", {
        id: "start_date",
        header: () => "Start",
        cell: (ctx) => formatDate(ctx.getValue()) || "-",
      }),
      col.accessor((r) => r.deadline_date ?? "", {
        id: "deadline_date",
        header: () => "Deadline",
        cell: (ctx) => formatDate(ctx.getValue()) || "-",
      }),
      col.display({
        id: "state",
        header: () => "Stan",
        cell: (ctx) => {
          const r = ctx.row.original;
          if (r.ended) return <span className={styles.stateEnded}>Zakończony</span>;
          if (r.stop_reason)
            return (
              <span className={styles.stateStopped} title={r.stop_reason}>
                Wstrzymany
              </span>
            );
          if (r.received) return <span className={styles.stateReceived}>Odebrany</span>;
          return <span className={styles.stateActive}>W toku</span>;
        },
      }),
      col.display({
        id: "tasks",
        header: () => "Zadania",
        cell: (ctx) => {
          const r = ctx.row.original;
          /* Linki, nie przyciski - każdy widok ma własny adres, więc klient może
             otworzyć go w nowej karcie albo przesłać dalej. Zwykłe kliknięcie
             przejmujemy (zmiana hasha nie przeładowuje Server Components),
             modyfikatory zostawiamy przeglądarce. */
          const intercept = (go: () => void) => (e: React.MouseEvent) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            go();
          };
          return (
            <span className={styles.rowActions}>
              <a
                className={styles.rowButton}
                href={pluginRouteHref({ view: "tasks", projectId: r.id })}
                onClick={intercept(() => onOpenTasks(r.id))}
                title={`Zadania projektu ${r.nr}`}
              >
                <Icon name="list-checks" />
                Zadania
              </a>
              {/* Zakończony projekt nie przyjmuje nowych zgłoszeń - backend i tak
                  by je odrzucił na etapie bramek, więc nie kusimy przyciskiem. */}
              {!r.ended && (
                <a
                  className={styles.rowButton}
                  href={pluginRouteHref({ view: "add", projectId: r.id })}
                  onClick={intercept(() => onAddTask(r.id))}
                  title={`Dodaj zadanie w projekcie ${r.nr}`}
                >
                  <Icon name="plus" />
                  Dodaj
                </a>
              )}
            </span>
          );
        },
      }),
    ];
  }, [onOpenTasks, onAddTask]);

  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;

  return (
    <div className={styles.root}>
      <p className={styles.note}>Projekty w których uczestniczysz.</p>

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <Field label="Stan" className={styles.filterField}>
            <Select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value as StateFilter)}
            >
              <option value={STATE_ACTIVE}>Aktywne</option>
              <option value={STATE_ENDED}>Zakończone</option>
              <option value={STATE_ALL}>Wszystkie</option>
            </Select>
          </Field>
          {stageOptions.length > 1 && (
            <Field label="Etap" className={styles.filterField}>
              <Select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
                <option value={STAGE_ALL}>Wszystkie</option>
                {stageOptions.map(([value, label]) => (
                  <option key={value} value={String(value)}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {hasActiveFilters && (
            <button
              type="button"
              className={styles.clearFilters}
              onClick={() => {
                setStateFilter(STATE_ACTIVE);
                setStageFilter(STAGE_ALL);
              }}
            >
              <Icon name="x" />
              Wyczyść filtry
            </button>
          )}
        </div>
        <div className={styles.searchWrap}>
          <Input
            type="search"
            prefix={<Icon name="magnifying-glass" />}
            placeholder="Szukaj (nr, nazwa, etap, rola)..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            aria-label="Szukaj projektów"
          />
        </div>
      </div>

      {loading && <FormStatus variant="info">Wczytywanie projektów...</FormStatus>}
      {error && !loading && <FormStatus variant="error">{error}</FormStatus>}

      {!loading && !error && rows.length === 0 && (
        <FormStatus variant="info">Nie uczestniczysz jeszcze w żadnym projekcie.</FormStatus>
      )}

      {!loading && !error && rows.length > 0 && filteredRows.length === 0 && (
        <FormStatus variant="info">Brak projektów spełniających wybrane filtry.</FormStatus>
      )}

      {!loading && !error && filteredRows.length > 0 && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => {
                      const canSort = header.column.getCanSort();
                      const sorted = header.column.getIsSorted();
                      return (
                        <th
                          key={header.id}
                          className={canSort ? styles.thSortable : undefined}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          aria-sort={
                            sorted === "asc"
                              ? "ascending"
                              : sorted === "desc"
                                ? "descending"
                                : undefined
                          }
                        >
                          <span className={styles.thInner}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && (
                              <Icon
                                name={sorted === "desc" ? "caret-down" : "caret-up"}
                                className={sorted ? styles.sortIconActive : styles.sortIcon}
                              />
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {truncated && (
            <p className={styles.truncatedHint}>
              Wyświetlono pierwsze 300 projektów. Zawęź wyszukiwanie aby znaleźć pozostałe.
            </p>
          )}

          {pageCount > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <Icon name="caret-left" />
                Poprzednia
              </button>
              <span className={styles.pageInfo}>
                Strona {pageIndex + 1} z {pageCount}
              </span>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Następna
                <Icon name="caret-right" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
